import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from '@mui/material';
import TaskTile from './TaskTile';
import PermissionGate from '../../common/PermissionGate';
import { AuthContext } from '../../../contexts/AuthContext';
import { isTaskLocked, getLockedTooltip } from '../../../utils/taskDependencies';

/**
 * Mapping of tile IDs to their system-level permission paths.
 * Follows the existing convention: foundation.{section}.{tile}
 */
const TILE_PERMISSION_MAP = {
  // Analysis
  'project-info': 'foundation.analysis.project_info',
  'chat-prd': 'foundation.analysis.project_info',
  'features': 'foundation.analysis.features',
  'feature-info': 'foundation.analysis.features',
  'change-request-info': 'foundation.analysis.features',
  'user-roles': 'foundation.analysis.user_roles',
  'user-stories': 'foundation.analysis.user_stories',
  'process-flows': 'foundation.analysis.process_flows',

  // UI/UX
  'uiux-references': 'foundation.uiux.uiux_references',
  'design-references': 'foundation.uiux.uiux_references',
  'wireframes': 'foundation.uiux.wireframes',
  'uiux-guidelines': 'foundation.uiux.uiux_guidelines',
  'design-guidelines': 'foundation.uiux.uiux_guidelines',
  'uiux-style-guide': 'foundation.uiux.uiux_style_guide',
  'uiux-layout': 'foundation.uiux.uiux_layout',

  // Technical
  'field-validations': 'foundation.technical.field_validations',
  'tech-stack': 'foundation.technical.technology_stack',
  'architecture': 'foundation.technical.architecture',
  'dev-guidelines': 'foundation.technical.development_guidelines',
  'api-guidelines': 'foundation.technical.api_guidelines',
  'security-guidelines': 'foundation.technical.security_guidelines',
  'user-permissions': 'foundation.technical.user_permissions',
  'unit-testing': 'foundation.technical.unit_testing',

  // Quality
  'functional-testing': 'foundation.quality.functional_testing',
  'automated-testing': 'foundation.quality.automated_testing',
  'security-testing': 'foundation.quality.security_testing',
  'performance-testing': 'foundation.quality.performance_testing',

  // Infrastructure
  'git-guidelines': 'foundation.infrastructure.code_version_control',
  'code-version-control': 'foundation.infrastructure.code_version_control',
  'cicd-pipeline': 'foundation.infrastructure.cicd_pipeline',
  'ci-cd-pipeline': 'foundation.infrastructure.cicd_pipeline',
  'deployment': 'foundation.infrastructure.deployment',
};

/**
 * Mapping of tile titles (lowercase) to permission paths.
 * Fallback when tile IDs don't match TILE_PERMISSION_MAP (e.g. MongoDB ObjectIds).
 */
const TILE_TITLE_PERMISSION_MAP = {
  'project info': 'foundation.analysis.project_info',
  'features': 'foundation.analysis.features',
  'user roles': 'foundation.analysis.user_roles',
  'user stories': 'foundation.analysis.user_stories',
  'process flows': 'foundation.analysis.process_flows',
  'uiux references': 'foundation.uiux.uiux_references',
  'ui/ux references': 'foundation.uiux.uiux_references',
  'design references': 'foundation.uiux.uiux_references',
  'wireframes': 'foundation.uiux.wireframes',
  'uiux guidelines': 'foundation.uiux.uiux_guidelines',
  'ui/ux guidelines': 'foundation.uiux.uiux_guidelines',
  'design guidelines': 'foundation.uiux.uiux_guidelines',
  'uiux style guide': 'foundation.uiux.uiux_style_guide',
  'ui/ux style guide': 'foundation.uiux.uiux_style_guide',
  'uiux layout': 'foundation.uiux.uiux_layout',
  'ui/ux layout': 'foundation.uiux.uiux_layout',
  'field validations': 'foundation.technical.field_validations',
  'technology stack': 'foundation.technical.technology_stack',
  'tech stack': 'foundation.technical.technology_stack',
  'architecture': 'foundation.technical.architecture',
  'development guidelines': 'foundation.technical.development_guidelines',
  'dev guidelines': 'foundation.technical.development_guidelines',
  'api guidelines': 'foundation.technical.api_guidelines',
  'security guidelines': 'foundation.technical.security_guidelines',
  'user permissions': 'foundation.technical.user_permissions',
  'unit testing': 'foundation.technical.unit_testing',
  'functional testing': 'foundation.quality.functional_testing',
  'automated testing': 'foundation.quality.automated_testing',
  'security testing': 'foundation.quality.security_testing',
  'performance testing': 'foundation.quality.performance_testing',
  'ci/cd pipeline': 'foundation.infrastructure.cicd_pipeline',
  'cicd pipeline': 'foundation.infrastructure.cicd_pipeline',
  'deployment': 'foundation.infrastructure.deployment',
  'git/version control': 'foundation.infrastructure.code_version_control',
  'code version control': 'foundation.infrastructure.code_version_control',
  'version control': 'foundation.infrastructure.code_version_control',
};

/**
 * Get permission path for a tile — checks ID fields first, then falls back to title.
 */
const getTilePermission = (tile) => {
  // 1. Check all possible ID fields against TILE_PERMISSION_MAP
  const possibleIds = [tile.id, tile.task_id, tile.taskId, tile._id];
  for (const id of possibleIds) {
    if (id && TILE_PERMISSION_MAP[id]) {
      return TILE_PERMISSION_MAP[id];
    }
  }
  // 2. Fallback: match by title (handles cases where IDs are MongoDB ObjectIds)
  if (tile.title) {
    const titleKey = tile.title.toLowerCase().trim();
    if (TILE_TITLE_PERMISSION_MAP[titleKey]) {
      return TILE_TITLE_PERMISSION_MAP[titleKey];
    }
  }
  return null;
};

/**
 * Grid component for displaying task tiles
 * @param {Object} props - Component props
 * @param {Array} props.sectionTiles - Array of task tiles to display
 * @param {Function} props.onTileClick - Function to handle tile click
 * @param {Function} props.onMenuOpen - Function to open the menu
 * @returns {React.ReactElement} Tasks grid component
 */
const TasksGrid = ({ sectionTiles, onTileClick, onMenuOpen, validationSummary, completedTasks }) => {
  const { hasPermission } = useContext(AuthContext);

  // Check if at least one tile in the section is visible
  const hasVisibleTiles = useMemo(() => {
    return sectionTiles.some((tile) => {
      const permission = getTilePermission(tile);
      if (!permission) return true;
      return hasPermission(permission);
    });
  }, [sectionTiles, hasPermission]);

  if (!hasVisibleTiles) return null;

  return (
    <Box sx={{ flex: 1, py: 2, mt: 1 }}>
      <Grid container spacing={5}>
        {sectionTiles.map((tile) => {
          const permission = getTilePermission(tile);
          const tileId = tile.id || tile.task_id || tile._id;
          // TODO: Re-enable tile locking once dependency flow is finalized
          // const locked = isTaskLocked(tileId, completedTasks || []);
          // const lockedTooltip = locked ? getLockedTooltip(tileId, completedTasks || []) : '';
          const locked = false;
          const lockedTooltip = '';

          // No permission mapping — render without gate
          if (!permission) {
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={tile.id}>
                <TaskTile
                  tile={tile}
                  onClick={onTileClick}
                  onMenuOpen={onMenuOpen}
                  validationInfo={validationSummary?.[tile.id]}
                  disabled={locked}
                  disabledTooltip={lockedTooltip}
                />
              </Grid>
            );
          }

          // Wrap with PermissionGate
          return (
            <PermissionGate key={tile.id} permission={permission}>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <TaskTile
                  tile={tile}
                  onClick={onTileClick}
                  onMenuOpen={onMenuOpen}
                  validationInfo={validationSummary?.[tile.id]}
                  disabled={locked}
                  disabledTooltip={lockedTooltip}
                />
              </Grid>
            </PermissionGate>
          );
        })}
      </Grid>
    </Box>
  );
};

TasksGrid.propTypes = {
  sectionTiles: PropTypes.array.isRequired,
  onTileClick: PropTypes.func.isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  validationSummary: PropTypes.object,
  completedTasks: PropTypes.array
};

export { TILE_PERMISSION_MAP, getTilePermission };
export default React.memo(TasksGrid);
